/**
 * CAOS-379 — the WAYS a list can be shown, and which of them a given list may offer.
 *
 * Every list in our own apps is the same list: the same controls, the same options, whether it holds
 * accounts, orders, or permission sets. What varies is not the surface but what the records will
 * SUPPORT — a board needs a field that says what stage a record is at, and grouping needs something
 * worth grouping by. So the offer is derived from the object, once, here, rather than each surface
 * deciding for itself and drifting.
 *
 * Two of the modes are always offered, because every set of records supports them: the CARD view
 * (the default — it is what a person reads first) and the TABLE. Split is offered too: a lane of
 * records beside the one you are reading works for any object. Board and Group are conditional, and
 * a mode that is not offered is not drawn — a control that appears and then explains it cannot do
 * anything is worse than one that was never there.
 */
import type { FieldMeta, ObjectMeta, OrgMetadata } from '../metadata-source.js';

/** How a list is being shown. */
export type ListMode = 'card' | 'table' | 'split' | 'kanban' | 'group';

/** The mark and the words each mode's control carries. */
export const MODE_LABEL: Readonly<Record<ListMode, { icon: string; label: string }>> = {
  card: { icon: 'layout-grid', label: 'Cards' },
  table: { icon: 'table-2', label: 'Table' },
  kanban: { icon: 'kanban', label: 'Board' },
  split: { icon: 'columns-2', label: 'Split' },
  group: { icon: 'layers', label: 'Grouped' },
};

/** The view a list opens on when nobody has chosen otherwise. */
export const DEFAULT_MODE: ListMode = 'card';

/** Whether a field could stand a board's columns up: a Select with a populated value set. */
function isStageCapable(field: FieldMeta, meta: OrgMetadata): boolean {
  if (field.storageType !== 'Select' || !field.valueSet) return false;
  const options = meta.valueSets.get(field.valueSet);
  return options != null && options.size > 0;
}

/**
 * The field a BOARD would stand its columns up from: a Select whose value set the workspace
 * knows, preferring one that names itself a status or a stage. Null ⇒ these records have no
 * stages, so no board is offered — a board with one column is a worse table.
 *
 * CAOS-467 — `declared` is the list view's own `groupBy` (bare api name), when it has one. This
 * function stays the CAPABILITY gate (a board is offered at all only when SOME stage-capable
 * field exists — that decision is unchanged), but the field a board actually stands its columns
 * up from now reads a valid declared choice first, falling back to the same inference as before
 * only when the list view named nothing, or named a field that is not stage-capable.
 */
export function stageFieldFor(object: ObjectMeta, meta: OrgMetadata, declared?: string): FieldMeta | null {
  if (declared) {
    const field = object.fields.get(declared);
    if (field && isStageCapable(field, meta)) return field;
  }
  const candidates: FieldMeta[] = [];
  for (const field of object.fields.values()) {
    if (isStageCapable(field, meta)) candidates.push(field);
  }
  if (candidates.length === 0) return null;
  const named = candidates.find((f) => /status|stage|phase/i.test(f.apiName));
  return named ?? candidates[0]!;
}

/**
 * The fields a GROUPED view could gather records under: the same Select fields a board could use,
 * plus the lookups (grouping orders by account is the question people actually ask). Empty ⇒ no
 * grouped view is offered.
 */
export function groupFieldsFor(object: ObjectMeta, meta: OrgMetadata): readonly FieldMeta[] {
  const out: FieldMeta[] = [];
  for (const field of object.fields.values()) {
    if (field.system) continue;
    if (field.storageType === 'Select' && field.valueSet && (meta.valueSets.get(field.valueSet)?.size ?? 0) > 0) out.push(field);
    else if (field.storageType === 'Reference') out.push(field);
  }
  return out;
}

/**
 * Remember the view a person chose, per object, for as long as the tab is open.
 *
 * Per OBJECT rather than per workspace, because the answer is a property of the records: a board is
 * the right way to read orders and the wrong way to read accounts, and being thrown into whichever
 * one you last used somewhere else is disorienting. Session-scoped rather than saved, because it is
 * a preference about right now, not a setting somebody has to go and undo later.
 */
const MODE_KEY = 'caos.list-mode.';

/**
 * CAOS-467 — `fallback` is what this PARTICULAR list opens on when nobody has chosen otherwise: the
 * list view's own declared `presentation` when it named one the object can actually offer, else the
 * same platform default (`card`) as before. A person's own choice, remembered above, still wins over
 * either — this only decides what a list nobody has touched yet opens on.
 */
export function rememberedMode(
  objectKey: string,
  offered: readonly ListMode[],
  fallback: ListMode = DEFAULT_MODE,
): ListMode {
  try {
    const stored = globalThis.sessionStorage?.getItem(MODE_KEY + objectKey);
    if (stored && offered.includes(stored as ListMode)) return stored as ListMode;
  } catch {
    // A workspace with storage denied still gets a list; it just opens on the default every time.
  }
  return offered.includes(fallback) ? fallback : offered[0]!;
}

export function rememberMode(objectKey: string, mode: ListMode): void {
  try {
    globalThis.sessionStorage?.setItem(MODE_KEY + objectKey, mode);
  } catch {
    // Not remembering is a smaller failure than not showing the list.
  }
}
